// src/pages/Cloud/charts/ComplianceDetailTable.js
import React, { useMemo, useState, useRef } from 'react';
import { Table, Spinner, Offcanvas, Card, Row, Col, Image } from 'react-bootstrap';
import { Tabs, Tab, Box, IconButton } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FSCInlineSingleEditFromTable from '../components/fsc/FSCInlineSingleEditFromTable';
import { isFscControlNode } from '../components/fsc/fscEditClauseUtils';
import RichText from '../../../components/Common/RichText';
import './styles/ComplianceDetailTable.scss';

function ComplianceDetailTable({
  controls = [],
  assessmentResources = {},
  loading = false,
  resourcesLoading = false,
  onLoadResources,
  projectUuid = null,
  months = [],
  currentMonthKey = null,
  variant = 'default', // 'default' | 'fsc'
  canEditFsc = false,
  /** 合規資源圖 API 平台（例如 gcp）：用於 GCP finding id 不再含 `/findings/` 時判斷建議欄位來源 */
  compliancePlatform = null,
  cloudScope = null,
}) {
  const isFscPage = variant === 'fsc';
  const fscInlineEditRef = useRef(null);

  const [expandedControls, setExpandedControls] = useState({});
  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [selectedControl, setSelectedControl] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const monthKeys = useMemo(() => (Array.isArray(months) ? months : []), [months]);

  // 展開／React key：跨 CIS／FSC 不可只靠 control_id（兩邊常有相同編號）
  const getControlExpandKey = (node) => {
    if (node?.uuid != null && String(node.uuid) !== '') {
      return `uuid:${node.uuid}`;
    }
    const cid = String(node?.control_id ?? '');
    const std = node?.standard_key;
    if (std) return `${std}:${cid}`;
    return cid;
  };

  // 顯示用月份：YYYY-MM -> M月（hover 仍可用 title 顯示完整 YYYY-MM）
  const formatMonthLabel = (monthKey) => {
    const s = String(monthKey || '');
    const m = s.match(/^(\d{4})-(\d{2})$/);
    if (!m) return s;
    const monthNum = parseInt(m[2], 10);
    if (!Number.isFinite(monthNum) || monthNum < 1 || monthNum > 12) return s;
    return `${monthNum}月`;
  };

  const effectiveCurrentMonthKey = useMemo(() => {
    if (currentMonthKey) return currentMonthKey;
    return monthKeys.length > 0 ? monthKeys[monthKeys.length - 1] : null;
  }, [currentMonthKey, monthKeys]);

  const fscOnlyControls = useMemo(
    () => (Array.isArray(controls) ? controls.filter(isFscControlNode) : []),
    [controls]
  );

  const toggleControl = (expandKey) => {
    setExpandedControls((prev) => ({
      ...prev,
      [expandKey]: !prev[expandKey],
    }));
  };

  const handleTabChange = (_event, newValue) => {
    setTabValue(newValue);
  };

  // 只在 FSC 頁面且點到的是 FSC control 才精簡
  const isSlimSidebar = useMemo(() => {
    if (!isFscPage) return false;
    return isFscControlNode(selectedControl);
  }, [isFscPage, selectedControl]);

  // Azure 過去月份改讀 DB 該月掃描結果（assessment 可能已被 Azure 移除，即時查詢會查不到建議/補救步驟）
  // GCP 一列是一條 Rule、AWS 一列是一個控制項，過去月份需帶月份才能取到該月的受影響資源
  const getResourceScanMonth = (monthKey) => {
    const plat = String(compliancePlatform || 'azure').toLowerCase();
    if (plat !== 'azure' && plat !== 'gcp' && plat !== 'aws') return null;
    if (!monthKey || monthKey === effectiveCurrentMonthKey) return null;
    return monthKey;
  };

  // Azure 同名合併列的各月份可能是不同 assessment key，查資源以當月的 id 為準
  const getMonthAssessmentId = (assessment, monthData) =>
    monthData?.assessment_id || assessment?.assessment_id || null;

  const getResourceKey = (assessment) => {
    const assessmentId = getMonthAssessmentId(assessment, assessment?.selectedMonthData);
    if (!assessmentId) return null;
    const scanMonth = getResourceScanMonth(assessment.selectedMonthKey);
    return scanMonth ? `${assessmentId}|${scanMonth}` : assessmentId;
  };

  const handleOpenSidebar = (control, assessment, monthData, monthKey) => {
    const nextAssessment = {
      ...assessment,
      selectedMonthData: monthData,
      selectedMonthKey: monthKey,
    };
    setSelectedControl(control);
    setSelectedAssessment(nextAssessment);
    setShowSidebar(true);
    setTabValue(0);

    // 即使在 FSC 頁面，CIS 的 assessment 也載入 resources
    // FSC control（手動評估）不載入 resources
    if (assessment?.assessment_id && !isFscControlNode(control)) {
      if (!assessmentResources[getResourceKey(nextAssessment)] && typeof onLoadResources === 'function') {
        onLoadResources(
          getMonthAssessmentId(assessment, monthData),
          assessment.assessment_name,
          getResourceScanMonth(monthKey)
        );
      }
    }
  };

  const handleCloseSidebar = () => {
    setShowSidebar(false);
    setSelectedAssessment(null);
    setSelectedControl(null);
  };

  const getMonthStatus = (assessment, monthKey) => {
    if (!assessment?.monthly_status) return null;
    const monthData = assessment.monthly_status?.[monthKey];
    if (!monthData) return null;

    return {
      assessment_id: monthData.assessment_id,
      state: monthData.state, // Passed / Failed / Skipped / Partial
      passed: monthData.passed || 0,
      failed: monthData.failed || 0,
      skipped: monthData.skipped || 0,
      total: monthData.total || 0,
      source: monthData.source,

      // FSC 額外欄位（若有）
      raw_status: monthData.raw_status,
      status_display: monthData.status_display,
      recommendation: monthData.recommendation,
      remediation_steps: monthData.remediation_steps,
      assessment_date: monthData.assessment_date,
      notes: monthData.notes,
    };
  };

  const getNodeComplianceStatus = (node) => {
    if (node?.has_assessments && Array.isArray(node.assessments) && node.assessments.length > 0) {
      for (const assessment of node.assessments) {
        const monthData = assessment?.monthly_status?.[effectiveCurrentMonthKey];
        if (monthData && (monthData.state === 'Failed' || monthData.state === 'Partial')) {
          return 'failed';
        }
      }
    }

    if (Array.isArray(node?.children) && node.children.length > 0) {
      for (const child of node.children) {
        const childStatus = getNodeComplianceStatus(child);
        if (childStatus === 'failed') return 'failed';
      }
    }

    return 'passed';
  };

  const renderComplianceIcon = (node) => {
    const status = getNodeComplianceStatus(node);
    if (status === 'failed') {
      return (
        <CancelIcon
          sx={{
            fontSize: 18,
            color: '#C5283D',
            marginRight: '8px',
          }}
        />
      );
    }
    return (
      <CheckCircleIcon
        sx={{
          fontSize: 18,
          color: '#28a745',
          marginRight: '8px',
        }}
      />
    );
  };

  const renderMonthCell = (control, assessment, monthKey) => {
    const status = getMonthStatus(assessment, monthKey);

    if (!status) {
      return <td key={monthKey} className="month-cell empty"></td>;
    }

    const isCurrentMonth = monthKey === effectiveCurrentMonthKey;
    const isPassed = status.state === 'Passed';
    const isFailed = status.state === 'Failed';
    const isPartial = status.state === 'Partial';
    const isSkipped = status.state === 'Skipped' || status.skipped > 0;

    let cellClass = 'month-cell';
    if (isPassed) cellClass += ' passed';
    else if (isFailed) cellClass += ' failed';
    else if (isPartial) cellClass += ' partial';
    else if (isSkipped) cellClass += ' skipped';
    if (isCurrentMonth) cellClass += ' current';

    const inlineStyle = isPartial
      ? { cursor: 'pointer', backgroundColor: '#F2C94C', color: '#212529' }
      : { cursor: 'pointer' };

    const titleText = `${status.passed}/${status.total}`;

    return (
      <td
        key={monthKey}
        className={cellClass}
        onClick={() => handleOpenSidebar(control, assessment, status, monthKey)}
        style={inlineStyle}
        title={titleText}
      >
        {isPassed && (
          <>
            <span className="status-text">通過</span>
            <InfoOutlinedIcon sx={{ fontSize: 12, marginLeft: '2px', verticalAlign: 'middle' }} />
          </>
        )}
        {isFailed && (
          <>
            <span className="status-text">未通過</span>
            <InfoOutlinedIcon sx={{ fontSize: 12, marginLeft: '2px', verticalAlign: 'middle' }} />
          </>
        )}
        {isPartial && (
          <>
            <span className="status-text">部分通過</span>
            <InfoOutlinedIcon sx={{ fontSize: 12, marginLeft: '2px', verticalAlign: 'middle' }} />
          </>
        )}
        {isSkipped && !isPassed && !isFailed && !isPartial && (
          <>
            <span className="status-text">不適用</span>
            <InfoOutlinedIcon sx={{ fontSize: 12, marginLeft: '2px', verticalAlign: 'middle' }} />
          </>
        )}
      </td>
    );
  };

  const getSelectedRawStatus = () => {
    const md = selectedAssessment?.selectedMonthData;
    if (!md) return null;

    if (md.raw_status) return String(md.raw_status).toLowerCase();

    const s = String(md.state || '').toLowerCase();
    if (s === 'passed') return 'passed';
    if (s === 'failed') return 'failed';
    if (s === 'partial') return 'partial';
    if (s === 'skipped') return 'skipped';
    return null;
  };

  // chip_state_* svg（只顯示 icon，避免狀態文字重複）
  const renderStatusChipIcon = () => {
    const raw = getSelectedRawStatus();
    if (!raw) return null;

    let icon = null;
    let alt = '';

    if (raw === 'passed') {
      icon = '/assets/images/chip_state_success.svg';
      alt = 'chip_state_success';
    } else if (raw === 'failed') {
      icon = '/assets/images/chip_state_dangerous.svg';
      alt = 'chip_state_dangerous';
    } else if (raw === 'partial') {
      icon = '/assets/images/chip_state_warning.svg';
      alt = 'chip_state_warning';
    } else {
      return null;
    }

    return <Image alt={alt} src={icon} style={{ height: '22px', width: 'auto', display: 'inline' }} />;
  };

  // ====== default 模式（CIS/ISO/MCSB）：風險/資源 ======

  const calculateRiskStats = () => {
    if (!selectedAssessment?.selectedMonthData) {
      return { good: 0, notGood: 0, notApplicable: 0, total: 0 };
    }

    const data = selectedAssessment.selectedMonthData;
    return {
      good: data.passed || 0,
      notGood: data.failed || 0,
      notApplicable: data.skipped || 0,
      total: data.total || 0,
    };
  };

  // 資源層 metadata 為主；沒有受評估資源時以建議定義（assessment_metadata，與 Azure Portal 同源）補齊
  const getSelectedMetadata = (resourceData) => {
    const resourceMetadata = resourceData?.data?.[0]?.properties?.metadata || {};
    const fallback = resourceData?.assessment_metadata || {};
    const merged = { ...resourceMetadata };
    Object.keys(fallback).forEach((key) => {
      if (!merged[key] && fallback[key]) merged[key] = fallback[key];
    });
    return merged;
  };

  const getRiskLevel = () => {
    if (!selectedAssessment?.assessment_id) {
      return { level: 'Unknown', color: '#6c757d', loading: false };
    }

    const resourceData = assessmentResources[getResourceKey(selectedAssessment)];

    if (!resourceData && resourcesLoading) {
      return { level: '載入中...', color: '#6c757d', loading: true };
    }

    const severity = getSelectedMetadata(resourceData).severity;
    if (severity) {
      switch (severity) {
        // AWS 控制項有 Critical 等級
        case 'Critical':
        case 'High':
          return {
            level: severity,
            color: '#dc3545',
            icon: (
              <Image
                alt="high-risks"
                src="/assets/images/ic_trending_up_FILL0_wght400_GRAD0_opsz24.svg"
                style={{ display: 'inline' }}
              />
            ),
            loading: false,
          };
        case 'Medium':
          return {
            level: 'Medium',
            color: '#ffc107',
            icon: (
              <Image
                alt="medium-risks"
                src="/assets/images/ic_read_more_FILL0_wght400_GRAD0_opsz24.svg"
                style={{ display: 'inline' }}
              />
            ),
            loading: false,
          };
        case 'Low':
          return {
            level: 'Low',
            color: '#28a745',
            icon: (
              <Image
                alt="low-risks"
                src="/assets/images/ic_trending_down_FILL0_wght400_GRAD0_opsz24.svg"
                style={{ display: 'inline' }}
              />
            ),
            loading: false,
          };
        default:
          return { level: severity, color: '#6c757d', loading: false };
      }
    }

    if (!selectedAssessment?.selectedMonthData) {
      return { level: 'Unknown', color: '#6c757d', loading: false };
    }

    const data = selectedAssessment.selectedMonthData;
    const state = data.state;

    // 取不到 severity 時不以評估狀態代替風險等級
    if (state === 'Passed' || state === 'Skipped') return { level: '—', color: '#6c757d', loading: false };
    // Azure 彙總類別（Recommendation category）沒有 severity，Portal 也不顯示，不以失敗率推算
    if (detectAssessmentSource(resourceData) === 'azure') return { level: '—', color: '#6c757d', loading: false };

    const total = data.total || 0;
    const failed = data.failed || 0;

    if (total === 0) return { level: 'Unknown', color: '#6c757d', loading: false };

    const failureRate = (failed / total) * 100;

    if (failureRate >= 50) {
      return {
        level: 'High',
        color: '#dc3545',
        icon: (
          <Image
            alt="high-risks"
            src="/assets/images/ic_trending_up_FILL0_wght400_GRAD0_opsz24.svg"
            style={{ display: 'inline' }}
          />
        ),
        loading: false,
      };
    } else if (failureRate >= 20) {
      return {
        level: 'Medium',
        color: '#ffc107',
        icon: (
          <Image
            alt="medium-risks"
            src="/assets/images/ic_read_more_FILL0_wght400_GRAD0_opsz24.svg"
            style={{ display: 'inline' }}
          />
        ),
        loading: false,
      };
    } else if (failureRate > 0) {
      return {
        level: 'Low',
        color: '#28a745',
        icon: (
          <Image
            alt="low-risks"
            src="/assets/images/ic_bar_chart_24dp_323232_FILL1_wght400_GRAD0_opsz24.svg"
            style={{ display: 'inline' }}
          />
        ),
        loading: false,
      };
    }

    return { level: 'Passed', color: '#28a745', loading: false };
  };

  const getCurrentResources = () => {
    if (!selectedAssessment?.assessment_id) return [];
    const resourceData = assessmentResources[getResourceKey(selectedAssessment)];
    if (!resourceData || !resourceData.data) return [];
    return resourceData.data;
  };

  const getFilteredResources = () => {
    const allResources = getCurrentResources();
    if (!selectedAssessment?.selectedMonthData) return allResources;

    const state = selectedAssessment.selectedMonthData.state;

    // Azure 資源清單需與「資源健康狀態」卡片一致：依評估狀態只列對應狀態的資源
    if (detectAssessmentSource(assessmentResources[getResourceKey(selectedAssessment)]) === 'azure') {
      const statusCode = { Failed: 'Unhealthy', Passed: 'Healthy', Skipped: 'NotApplicable' }[state];
      if (!statusCode) return allResources;
      return allResources.filter((resource) => resource.properties?.status?.code === statusCode);
    }

    if (state === 'Failed') {
      return allResources.filter((resource) => {
        const status = resource.properties?.status?.code;
        return status === 'Unhealthy' || status === 'NotApplicable';
      });
    }

    return allResources;
  };

  // Azure 的 properties.displayName 是建議名稱（與側欄標題相同），改顯示資源名稱（與 Portal 受影響資源一致）
  const getResourceDisplayName = (resource) => {
    if (detectAssessmentSource(assessmentResources[getResourceKey(selectedAssessment)]) === 'azure') {
      const details = resource.properties?.resourceDetails || {};
      const resourceId = details.ResourceId || details.NativeResourceId || resource.id || '';
      const name = details.ResourceName || String(resourceId).split('/').filter(Boolean).pop();
      if (name) return name;
    }
    return resource.properties?.displayName || resource.name || '未知資源';
  };

  const detectAssessmentSource = (resourceData) => {
    const first = resourceData?.data?.[0] || {};
    const assessmentId = String(selectedAssessment?.assessment_id || '').toLowerCase();
    const cloudProvider = String(
      first?.properties?.cloudProvider ||
        first?.properties?.resource?.cloudProvider ||
        first?.properties?.resource?.cloud_provider ||
        ''
    ).toLowerCase();

    if (cloudProvider.includes('google') || cloudProvider.includes('gcp')) return 'gcp';
    if (cloudProvider.includes('azure') || cloudProvider.includes('microsoft')) return 'azure';
    if (cloudProvider.includes('aws') || cloudProvider.includes('amazon')) return 'aws';

    const plat = String(compliancePlatform || '').toLowerCase();
    if (plat === 'gcp') return 'gcp';
    if (plat === 'azure') return 'azure';
    if (plat === 'aws') return 'aws';

    if (assessmentId.includes('/findings/')) return 'gcp';
    if (assessmentId.includes('microsoft.security/assessments')) return 'azure';

    return 'unknown';
  };

  const getRecommendation = () => {
    if (!selectedAssessment?.assessment_id) return null;

    const resourceData = assessmentResources[getResourceKey(selectedAssessment)];
    if (!resourceData) return null;

    const source = detectAssessmentSource(resourceData);
    const metadata = getSelectedMetadata(resourceData);
    let recommendation = null;
    if (source === 'gcp') {
      recommendation = metadata.recommendation;
    } else if (source === 'azure') {
      recommendation = metadata.description;
    } else if (source === 'aws') {
      recommendation = metadata.recommendation || metadata.description;
    } else {
      recommendation = metadata.description || metadata.recommendation;
    }
    if (!recommendation || recommendation.trim() === '') return null;

    return recommendation;
  };

  const getRemediationDescription = () => {
    if (!selectedAssessment?.assessment_id) return null;

    const resourceData = assessmentResources[getResourceKey(selectedAssessment)];
    if (!resourceData) return null;

    const source = detectAssessmentSource(resourceData);
    const metadata = getSelectedMetadata(resourceData);
    let remediationDescription = null;
    if (source === 'gcp') {
      remediationDescription = metadata.exceptionInstructions;
    } else if (source === 'azure') {
      remediationDescription = metadata.remediationDescription;
    } else if (source === 'aws') {
      remediationDescription = metadata.remediationDescription || metadata.exceptionInstructions;
    } else {
      remediationDescription = metadata.remediationDescription || metadata.exceptionInstructions;
    }
    if (!remediationDescription || remediationDescription.trim() === '') return null;

    return remediationDescription;
  };

  const getResourcesTabTitle = () => {
    if (!selectedAssessment?.selectedMonthData) return '相關資源';

    const state = selectedAssessment.selectedMonthData.state;

    if (state === 'Failed') return '狀況不良的資源';
    if (state === 'Passed') return '健康資源';
    if (state === 'Skipped') return '不適用的資源';

    return '相關資源';
  };

  // ====== FSC 精簡模式：建議/補救步驟直接讀 monthData ======

  const getFscRecommendation = () => {
    const md = selectedAssessment?.selectedMonthData;
    const text = md?.recommendation;
    if (!text || String(text).trim() === '') return null;
    return text;
  };

  const getFscRemediationSteps = () => {
    const md = selectedAssessment?.selectedMonthData;
    const text = md?.remediation_steps;
    if (!text || String(text).trim() === '') return null;
    return text;
  };

  const riskStats = calculateRiskStats();
  const riskLevel = getRiskLevel();
  const filteredResources = getFilteredResources();
  const recommendation = getRecommendation();
  const remediationDescription = getRemediationDescription();
  const resourcesTabTitle = getResourcesTabTitle();

  const fscRecommendation = getFscRecommendation();
  const fscRemediationSteps = getFscRemediationSteps();

  const renderControlNode = (node, depth = 0) => {
    const expandKey = getControlExpandKey(node);
    const isExpanded = expandedControls[expandKey];
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const hasAssessments = node.has_assessments && Array.isArray(node.assessments) && node.assessments.length > 0;

    const indentStyle = {
      paddingLeft: `${depth * 20 + 16}px`,
    };

    return (
      <React.Fragment key={expandKey}>
        <tr
          className={`control-row level-${depth}`}
          onClick={() => toggleControl(expandKey)}
          style={{ cursor: 'pointer' }}
        >
          <td className="control-name-cell">
            <div className="d-flex align-items-center" style={indentStyle}>
              {renderComplianceIcon(node)}
              {(hasChildren || hasAssessments) && (
                <>{isExpanded ? <ExpandMoreIcon className="me-1" /> : <ChevronRightIcon className="me-1" />}</>
              )}
              <div>
                <strong>{node.control_id}</strong>
                <span className="ms-2 text-muted">{node.control_name}</span>
              </div>
            </div>
          </td>
        </tr>

        {isExpanded && (
          <>
            {hasChildren && node.children.map((child) => renderControlNode(child, depth + 1))}

            {hasAssessments && (
              <tr className="assessment-row">
                <td className="p-0">
                  <table className="inner-table">
                    <colgroup>
                      <col className="col-name" />
                      {monthKeys.map((mk) => (
                        <col key={mk} className="col-month" />
                      ))}
                      <col className="col-action" />
                    </colgroup>

                    <thead>
                      <tr>
                        <th className="inner-name-header" style={{ paddingLeft: `${(depth + 1) * 20 + 16}px` }}>
                          名稱
                        </th>
                        {monthKeys.map((mk) => (
                          <th key={mk} className="month-header" title={mk}>
                            {formatMonthLabel(mk)}
                          </th>
                        ))}
                        <th className="action-header"></th>
                      </tr>
                    </thead>

                    <tbody>
                      {node.assessments.map((assessment, idx) => {
                        // FSC + is_assessable + leaf 才顯示更多
                        const showFscMoreMenu =
                          canEditFsc &&
                          isFscPage &&
                          isFscControlNode(node) &&
                          Boolean(node?.is_assessable) &&
                          !Boolean(node?.read_only) &&
                          (!Array.isArray(node?.children) || node.children.length === 0);

                        return (
                          <tr key={`${expandKey}-${assessment.assessment_id}-${idx}`}>
                            <td className="assessment-name-cell" style={{ paddingLeft: `${(depth + 1) * 20 + 16}px` }}>
                              <span className="assessment-name">{assessment.assessment_name}</span>
                            </td>

                            {monthKeys.map((mk) => renderMonthCell(node, assessment, mk))}

                            <td
                              className="action-cell"
                              style={{ textAlign: 'right' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {showFscMoreMenu ? (
                                <IconButton
                                  size="small"
                                  aria-label="更多操作"
                                  onClick={(e) => fscInlineEditRef.current?.openMenu(e, node)}
                                >
                                  <MoreVertIcon sx={{ color: '#BDBDBD', fontSize: 18 }} />
                                </IconButton>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </td>
              </tr>
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
        <p className="mt-2 text-muted">載入控制項資料中...</p>
      </div>
    );
  }

  if (!controls || controls.length === 0) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">目前沒有可用的控制項資料</p>
      </div>
    );
  }

  return (
    <>
      <div className={`compliance-detail-table ${isFscPage ? 'is-fsc' : ''}`}>
        <Table hover responsive className="modern-table">
          <tbody>{controls.map((control) => renderControlNode(control, 0))}</tbody>
        </Table>
      </div>

      <Offcanvas show={showSidebar} onHide={handleCloseSidebar} placement="end" style={{ width: '75%' }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <h4>分析與建議</h4>
          </Offcanvas.Title>
        </Offcanvas.Header>

        {selectedControl && selectedAssessment && (
          <div className="sidebar-subtitle px-4">
            <div className="d-flex align-items-center" style={{ gap: '10px', flexWrap: 'wrap' }}>
              <h5 className="control-title mb-0">
                {selectedControl.control_id} {selectedControl.control_name}
              </h5>
              {renderStatusChipIcon()}
            </div>

            <p className="assessment-title text-muted mb-0">{selectedAssessment.assessment_name}</p>
          </div>
        )}

        <Offcanvas.Body>
          {!isSlimSidebar && (
            <Row className="mb-4 g-3">
              <Col md={4}>
                <Card style={{ minHeight: '150px', height: '100%' }}>
                  <Card.Body className="d-flex flex-column justify-content-between align-items-center text-center p-4">
                    <div>
                      <span style={{ fontWeight: 'bold', color: '#5F5F5F' }}>風險等級</span>
                    </div>
                    <div className="mb-3">
                      {riskLevel.loading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <div className="d-flex align-items-center justify-content-center">
                          {riskLevel.icon && <span className="me-2">{riskLevel.icon}</span>}
                          <h2 style={{ fontWeight: 'bold', color: riskLevel.color, marginBottom: 0 }}>
                            {riskLevel.level}
                          </h2>
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={8}>
                <Card style={{ minHeight: '150px', height: '100%' }}>
                  <Card.Body className="p-4">
                    <div className="mb-3 text-center">
                      <span style={{ fontWeight: 'bold', color: '#5F5F5F' }}>資源健康狀態</span>
                    </div>
                    <Row className="text-center">
                      <Col xs={4}>
                        <h2 style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{riskStats.good}</h2>
                        <div className="d-flex align-items-center justify-content-center">
                          <Image
                            alt="good"
                            src="/assets/images/ic_check_circle_FILL0_wght400_GRAD0_opsz24.svg"
                            style={{ display: 'inline' }}
                          />
                          <span style={{ fontSize: '0.875rem', color: '#5F5F5F' }}>良好</span>
                        </div>
                      </Col>
                      <Col xs={4}>
                        <h2 style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{riskStats.notGood}</h2>
                        <div className="d-flex align-items-center justify-content-center">
                          <Image
                            alt="bad"
                            src="/assets/images/ic_cancel_FILL0_wght400_GRAD0_opsz24.svg"
                            style={{ display: 'inline' }}
                          />
                          <span style={{ fontSize: '0.875rem', color: '#5F5F5F' }}>狀況不良</span>
                        </div>
                      </Col>
                      <Col xs={4}>
                        <h2 style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{riskStats.notApplicable}</h2>
                        <div className="d-flex align-items-center justify-content-center">
                          <Image
                            alt="skipped"
                            src="/assets/images/ic_do_not_disturb_on_FILL0_wght400_GRAD0_opsz24.svg"
                            style={{ display: 'inline' }}
                          />
                          <span style={{ fontSize: '0.875rem', color: '#5F5F5F' }}>不適用</span>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  'button.Mui-selected': { color: '#7FBE25' },
                }}
                TabIndicatorProps={{ style: { backgroundColor: '#7FBE25' } }}
                aria-label="分析與建議標籤"
              >
                <Tab label="建議" />
                <Tab label="補救步驟" />
                {!isSlimSidebar && <Tab label={resourcesTabTitle} />}
              </Tabs>
            </Box>

            <Box sx={{ p: 2 }}>
              {tabValue === 0 && (
                <div className="tab-content">
                  <h6 className="mb-3">建議說明</h6>

                  {isSlimSidebar ? (
                    fscRecommendation ? (
                      <div className="content-description" style={{ whiteSpace: 'pre-wrap' }}>
                        {fscRecommendation}
                      </div>
                    ) : (
                      <p className="text-muted">無</p>
                    )
                  ) : resourcesLoading && !recommendation ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2 text-muted">載入中...</span>
                    </div>
                  ) : recommendation ? (
                    <RichText className="content-description" text={recommendation} />
                  ) : (
                    <p className="text-muted">無</p>
                  )}
                </div>
              )}

              {tabValue === 1 && (
                <div className="tab-content">
                  <h6 className="mb-3">補救步驟</h6>

                  {isSlimSidebar ? (
                    fscRemediationSteps ? (
                      <div className="remediation-content" style={{ whiteSpace: 'pre-wrap' }}>
                        {fscRemediationSteps}
                      </div>
                    ) : (
                      <p className="text-muted">無</p>
                    )
                  ) : resourcesLoading && !remediationDescription ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2 text-muted">載入中...</span>
                    </div>
                  ) : remediationDescription ? (
                    <RichText className="remediation-content" text={remediationDescription} />
                  ) : (
                    <p className="text-muted">無</p>
                  )}
                </div>
              )}

              {!isSlimSidebar && tabValue === 2 && (
                <div className="tab-content">
                  <h6 className="mb-3">
                    {resourcesTabTitle} ({filteredResources.length})
                  </h6>

                  {resourcesLoading && filteredResources.length === 0 ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2 text-muted">載入資源中...</span>
                    </div>
                  ) : filteredResources.length > 0 ? (
                    <div className="resources-table-container">
                      <table className="resources-table">
                        <thead>
                          <tr>
                            <th style={{ width: '60%' }}>資源名稱 / ID</th>
                            <th style={{ width: '40%' }}>資源類型</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredResources.map((resource, idx) => (
                            <tr key={idx}>
                              <td>
                                <div>
                                  <div className="resource-display-name">
                                    {getResourceDisplayName(resource)}
                                  </div>
                                  <code className="resource-id">
                                    {/* GCP 的 ResourceId 與 displayName 幾乎相同，改顯示 finding category */}
                                    {resource.properties?.category ||
                                      resource.properties?.resourceDetails?.ResourceId ||
                                      resource.properties?.resourceDetails?.NativeResourceId ||
                                      resource.id ||
                                      'N/A'}
                                  </code>
                                </div>
                              </td>
                              <td>
                                <span className="resource-type">
                                  {resource.properties?.resourceDetails?.ResourceType || resource.type || 'N/A'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-3">
                      <p className="text-muted mb-0">目前沒有資源資料</p>
                    </div>
                  )}
                </div>
              )}
            </Box>
          </Box>
        </Offcanvas.Body>
      </Offcanvas>
      <FSCInlineSingleEditFromTable
        key={`fsc-inline:${projectUuid}:${compliancePlatform}:${cloudScope}`}
        ref={fscInlineEditRef}
        controls={fscOnlyControls}
        projectUuid={projectUuid}
        fscCloud={compliancePlatform}
        cloudScope={cloudScope}
        effectiveCurrentMonthKey={effectiveCurrentMonthKey}
      />
    </>
  );
}

export default ComplianceDetailTable;
